import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteConfigProvider, useSiteConfig } from './context/SiteConfigContext';
import { ChurchProvider, useChurch } from './context/ChurchContext';
import useChurchSettings from './hooks/useChurchSettings';
import usePermissions from './hooks/usePermissions';

import Login from './pages/Login';
import FidelePortal from './pages/FidelePortal';
import ScanPresence from './pages/ScanPresence';
import Dashboard from './pages/Dashboard';
import Activities from './pages/Activities';
import ActivityDetail from './pages/ActivityDetail';
import Attendance from './pages/Attendance';
import Participants from './pages/Participants';
import Statistics from './pages/Statistics';
import Administration from './pages/Administration';
import MembershipAdmin from './pages/MembershipAdmin';
import SiteSettings from './pages/SiteSettings';
import LandingSettings from './pages/LandingSettings';
import ContactSubmissions from './pages/ContactSubmissions';
import NotificationsPush from './pages/NotificationsPush';
import NotificationsWhatsApp from './pages/NotificationsWhatsApp';
import Families from './pages/Families';
import Duplicates from './pages/Duplicates';
import FamilyReports from './pages/FamilyReports';
import Gallery from './pages/Gallery';
import UpdateBanner from './components/common/UpdateBanner';
import Import from './pages/Import';
import AuditLog from './pages/AuditLog';
import SuperAdmin from './pages/SuperAdmin';
import ChurchProfile from './pages/ChurchProfile';
import ParticipantDetail from './pages/ParticipantDetail';
import MobileAccueil from './pages/MobileAccueil';
import LandingPage from './pages/LandingPage';
import PublicGallery from './pages/PublicGallery';
import Accounting from './pages/Accounting';
import Permissions from './pages/Permissions';
import CardTemplates from './pages/CardTemplates';
import CardTemplateEditor from './pages/CardTemplateEditor';
import NotFound from './pages/NotFound';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import LoadingSpinner from './components/common/LoadingSpinner';

const styles = {
  appLayout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: 'var(--page-bg)',
  },
  mainWrapper: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px',
    minWidth: 0,
  },
};

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { config } = useSiteConfig();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 18, height: '100vh', background: config.primaryColor || '#000000' }}>
        <style>{`@keyframes appLoadPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: 0.85; } }`}</style>
        {config.logoUrl ? (
          <img src={config.logoUrl} alt="logo" style={{ width: 84, height: 84, borderRadius: 18, objectFit: 'cover', boxShadow: '0 8px 28px rgba(0,0,0,0.18)', animation: 'appLoadPulse 1.4s ease-in-out infinite' }} />
        ) : (
          <div style={{ animation: 'appLoadPulse 1.4s ease-in-out infinite' }}>
            <LoadingSpinner size={48} color="#fff" />
          </div>
        )}
        <LoadingSpinner size={34} color="#fff" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppLayout({ children }) {
  const { activeChurchId } = useChurch();
  const isMobile = () => window.innerWidth <= 640;
  const [sidebarOpen, setSidebarOpen] = React.useState(!isMobile());
  const location = useLocation();

  // Close sidebar on navigation on mobile
  React.useEffect(() => {
    if (isMobile()) setSidebarOpen(false);
  }, [location.pathname]);

  // Sync on resize
  React.useEffect(() => {
    const handler = () => {
      if (!isMobile()) setSidebarOpen(true);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const mobile = isMobile();

  return (
    <div style={styles.appLayout}>
      <Navbar onToggleSidebar={() => setSidebarOpen(o => !o)} />
      <div style={styles.mainWrapper}>
        {/* Overlay backdrop on mobile */}
        {mobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 49 }}
          />
        )}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          style={mobile
            ? { position: 'fixed', top: '60px', left: 0, bottom: 0, zIndex: 50 }
            : { position: 'sticky', top: 0, height: 'calc(100vh - 60px)', alignSelf: 'flex-start' }
          }
        />
        <main key={activeChurchId} style={{ ...styles.content, padding: mobile ? '16px' : '24px' }}>{children}</main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const { canSeeFinance, canSeeFideles, canSeeActivites, canSeeChurch, canSeeAdmin, canSeeNotifs, hasGroupAccess, isAdminOrResp } = usePermissions();
  // Réservé aux rôles admin (dont superviseur, mêmes droits qu'admin sur ses
  // églises assignées), jamais accordable via un groupe — gestion des
  // utilisateurs/permissions/paramètres, cf. Sidebar (même principe).
  const isAdmin = ['admin', 'superadmin_platform', 'superviseur'].includes(user?.role);
  const isSuperAdminPlatform = user?.role === 'superadmin_platform';
  const canImport = isAdminOrResp || hasGroupAccess('participants') || hasGroupAccess('activities');

  // Chef de Famille (RCC-RAP-001) : un seul écran, « Ma famille » ; toute autre
  // adresse de l'espace d'administration y renvoie (l'API le refuse de toute façon).
  if (user?.role === 'chef_famille') {
    return (
      <Routes>
        <Route path="/fidele" element={<FidelePortal />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/galerie-publique" element={<PublicGallery />} />
        <Route path="/ma-famille" element={<ProtectedRoute><AppLayout><FamilyReports /></AppLayout></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/ma-famille" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/fidele" element={<FidelePortal />} />
      <Route path="/scan" element={<ScanPresence />} />
      <Route path="/accueil-mobile" element={<MobileAccueil />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="/galerie-publique" element={<PublicGallery />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activites"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Activities />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activites/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ActivityDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/presences"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Attendance />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/participants"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Participants />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fideles"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Participants defaultStatus="exclude_invite" pageTitle="Fidèles" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/participants/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ParticipantDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/statistiques"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Statistics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comptabilite"
        element={
          <ProtectedRoute>
            <AppLayout>
              {canSeeFinance ? <Accounting /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/administration"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeAdmin) ? <Administration /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/adhesions"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdminOrResp || canSeeFideles) ? <MembershipAdmin /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parametres"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeChurch) ? <SiteSettings /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <AppLayout>
              {canImport ? <Import /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/landing-settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isSuperAdminPlatform ? <LandingSettings /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications-push"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeNotifs) ? <NotificationsPush /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications-whatsapp"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeNotifs) ? <NotificationsWhatsApp /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/familles"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdminOrResp || canSeeFideles) ? <Families /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapports-familles"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdminOrResp ? <FamilyReports /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doublons"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <Duplicates /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/galerie"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeActivites) ? <Gallery /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/demandes-contact"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isSuperAdminPlatform ? <ContactSubmissions /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/historique"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeNotifs) ? <AuditLog /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mon-eglise"
        element={
          <ProtectedRoute>
            <AppLayout>
              {(isAdmin || canSeeChurch) ? <ChurchProfile /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isSuperAdminPlatform ? <SuperAdmin /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/permissions"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <Permissions /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartes"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <CardTemplates /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartes/:templateId"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <CardTemplateEditor /> : <Navigate to="/dashboard" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={user ? <Navigate to="/dashboard" replace /> : <NotFound />} />
    </Routes>
  );
}

function DynamicFavicon() {
  const { activeChurchId } = useChurch();
  const churchSettings = useChurchSettings(activeChurchId);
  useEffect(() => {
    if (!churchSettings.logo) return;
    const link = document.querySelector("link[rel='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = churchSettings.logo;
    document.head.appendChild(link);
    // Also update apple-touch-icon
    const apple = document.querySelector("link[rel='apple-touch-icon']") || document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = churchSettings.logo;
    document.head.appendChild(apple);
  }, [churchSettings.logo]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ChurchProvider>
        <SiteConfigProvider>
        <DynamicFavicon />
        <UpdateBanner />
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#1e293b',
              color: '#f8fafc',
              borderRadius: '8px',
            },
            success: {
              iconTheme: { primary: '#22c55e', secondary: '#f8fafc' },
            },
            error: {
              iconTheme: { primary: '#ef4444', secondary: '#f8fafc' },
            },
          }}
        />
        </SiteConfigProvider>
        </ChurchProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
